import { toast } from 'react-toastify'
import { NUMBER_OF_ITEMS_PER_PAGE } from '../../constants'
import { getErrorMessage } from '../../helpers'
import {
  BaseApiResponse,
  QueryProps,
  StorefrontSection,
} from '../shared/shared.type'
import { apiSlice } from '../api/apiSlice'
import { ErrorType } from '../shared/shared.type'
import {
  type ArticleStatus,
  type CreateArticle,
  type CreateListBody,
  type CreateListResponseData,
  type ListArticlesResponse,
  type ListCategoryResponseData,
  type ListCommentsResponseData,
  type ListProductsResponseData,
  type ListResponseData,
  type ListsResponseData,
  type SingleListArticleResponse,
  DeleteListArticleResponse,
  EditedListArticleResponse,
} from './list.types'

export const listsApiSlice = apiSlice.injectEndpoints({
  overrideExisting: true,
  endpoints: (builder) => ({
    fetchLists: builder.query<
      ListsResponseData,
      QueryProps & {
        status?: string
        type?: 'LIFESTYLE' | 'FOOD' | 'EVENT'
      }
    >({
      query: (args) => ({
        url: `v1/lists`,
        method: 'GET',
        params: {
          ...args,
        },
      }),
      providesTags: [{ type: 'LIST', id: 'LIST' }],
      transformResponse: (data: ListsResponseData) => {
        return data
      },
    }),

    fetchListCategories: builder.query<
      ListCategoryResponseData,
      QueryProps & { isAdmin: boolean; section?: StorefrontSection }
    >({
      query: ({ limit, skip, search, isAdmin, section }) => ({
        url: `v1/items/categories`,
        method: 'GET',
        params: {
          skip: skip ?? 0,
          limit: limit ?? NUMBER_OF_ITEMS_PER_PAGE,
          search: search ?? undefined,
          isAdmin: isAdmin,
          section,
        },
      }),
      providesTags: [{ type: 'CATEGORY', id: 'LIST' }],
      transformResponse: (data: ListCategoryResponseData) => {
        return data
      },
    }),

    addProductsToList: builder.mutation<
      ListCategoryResponseData,
      { productIds: string[]; listId: string }
    >({
      query: ({ productIds, listId }) => ({
        url: `v1/lists/${listId}/items`,
        method: 'POST',
        body: { productIds },
      }),
      invalidatesTags: (_result, _error, arg) => [
        { type: 'LIST', id: arg.listId },
      ],
      transformResponse: (data: ListCategoryResponseData) => {
        return data
      },
      async onQueryStarted(arg, { dispatch, queryFulfilled }) {
        try {
          await queryFulfilled
          toast.success('Products added successfully')
        } catch (error) {
          toast.error(getErrorMessage(error as ErrorType))
        }
      },
    }),

    updateProductsInList: builder.mutation<
      ListCategoryResponseData,
      { productIds: string[]; listId: string }
    >({
      query: ({ productIds, listId }) => ({
        url: `v1/lists/${listId}/items`,
        method: 'PUT',
        body: { productIds },
      }),
      invalidatesTags: (_result, _error, arg) => [
        { type: 'LIST', id: arg.listId },
      ],
      transformResponse: (data: ListCategoryResponseData) => {
        return data
      },
      async onQueryStarted(arg, { dispatch, queryFulfilled }) {
        try {
          const data = await queryFulfilled
          toast.success(data.data.message)
        } catch (error) {
          toast.error(getErrorMessage(error as ErrorType))
        }
      },
    }),

    getListProducts: builder.query<ListProductsResponseData, string>({
      query: (listId) => ({
        url: `v1/lists/${listId}/items`,
        method: 'GET',
      }),
      providesTags: (_result, _error, arg) => [{ type: 'LIST', id: arg }],
      transformResponse: (data: ListProductsResponseData) => {
        return data
      },
    }),

    createList: builder.mutation<CreateListResponseData, CreateListBody>({
      query: (body) => ({
        url: `v1/lists`,
        method: 'POST',
        body,
      }),
      invalidatesTags: [{ type: 'LIST', id: 'LIST' }],
      transformResponse: (data: CreateListResponseData) => {
        return data
      },
      async onQueryStarted(arg, { dispatch, queryFulfilled }) {
        try {
          const { data } = await queryFulfilled
          toast.success(data.message)
        } catch (error) {
          toast.error(getErrorMessage(error as ErrorType))
        }
      },
    }),

    editList: builder.mutation<
      CreateListResponseData,
      { id: string; body: CreateListBody }
    >({
      query: ({ id, body }) => ({
        url: `v1/lists/${id}`,
        method: 'PUT',
        body,
      }),
      invalidatesTags: (_result, _error, arg) => [
        { type: 'LIST', id: arg.id },
        { type: 'LIST', id: 'LIST' },
      ],
      transformResponse: (data: CreateListResponseData) => {
        return data
      },
      async onQueryStarted(arg, { dispatch, queryFulfilled }) {
        try {
          const { data } = await queryFulfilled
          toast.success(data.message)
        } catch (error) {
          toast.error(getErrorMessage(error as ErrorType))
        }
      },
    }),

    pinUnpinList: builder.mutation<
      CreateListResponseData,
      { id: string; body: { pinned: boolean | string } }
    >({
      query: ({ id, body }) => ({
        url: `v1/lists/${id}`,
        method: 'PATCH',
        body,
      }),
      invalidatesTags: (_result, _error, arg) => [
        { type: 'LIST', id: arg.id },
        { type: 'LIST', id: 'LIST' },
      ],
      transformResponse: (data: CreateListResponseData) => {
        return data
      },
      async onQueryStarted(arg, { dispatch, queryFulfilled }) {
        try {
          const { data } = await queryFulfilled
          toast.success(data.message)
        } catch (error) {
          toast.error(getErrorMessage(error as ErrorType))
        }
      },
    }),

    moveList: builder.mutation<
      { data: null; message: string; success: boolean },
      { id: string; categoryId: string; newPosition: number }
    >({
      query: ({ id, categoryId, newPosition }) => ({
        url: `v1/lists/move/${id}/${categoryId}`,
        method: 'PATCH',
        body: {
          toIndex: newPosition,
        },
      }),
      invalidatesTags: (_result, _error, arg) => [
        { type: 'LIST', id: 'LIST' },
        { type: 'CATEGORY', id: arg.categoryId },
      ],
      async onQueryStarted(arg, { dispatch, queryFulfilled }) {
        try {
          const { data } = await queryFulfilled
          toast.success(data.message)
        } catch (error) {
          toast.error(getErrorMessage(error as ErrorType))
        }
      },
    }),

    reorderEverydayList: builder.mutation<
      BaseApiResponse,
      { listIds: string[] }
    >({
      query: (payload) => ({
        url: `v1/lists/reorder`,
        method: 'POST',
        body: {
          ...payload,
        },
      }),
      invalidatesTags: [{ type: 'LIST', id: 'LIST' }],
      async onQueryStarted(arg, { queryFulfilled }) {
        try {
          await queryFulfilled
        } catch (error) {
          toast.error(getErrorMessage(error as ErrorType))
        }
      },
    }),

    publishUnpublishList: builder.mutation<
      { data: null; message: string; success: boolean },
      { type: 'publish' | 'unpublish'; id: string }
    >({
      query: ({ id, type }) => ({
        url:
          type === 'publish'
            ? `v1/lists/publish/${id}`
            : `v1/lists/unpublish/${id}`,
        method: 'PATCH',
      }),
      invalidatesTags: (_result, _error, arg) => [
        { type: 'LIST', id: arg.id },
        { type: 'LIST', id: 'LIST' },
      ],
      async onQueryStarted(arg, { dispatch, queryFulfilled }) {
        try {
          const { data } = await queryFulfilled
          toast.success(data.message)
        } catch (error) {
          toast.error(getErrorMessage(error as ErrorType))
        }
      },
    }),

    fetchList: builder.query<ListResponseData, { id: string }>({
      query: ({ id }) => ({
        url: `v1/lists/${id}/details`,
        method: 'GET',
      }),
      providesTags: (_result, _error, arg) => [{ type: 'LIST', id: arg.id }],
      keepUnusedDataFor: 0,
      transformResponse: (data: ListResponseData) => {
        return data
      },
    }),

    deleteList: builder.mutation<{ success: boolean }, string>({
      query: (id) => ({
        url: `v1/lists/${id}`,
        method: 'DELETE',
      }),
      invalidatesTags: (_result, _error, arg) => [
        { type: 'LIST', id: arg },
        { type: 'LIST', id: 'LIST' },
      ],
      async onQueryStarted(arg, { dispatch, queryFulfilled }) {
        try {
          const { data } = await queryFulfilled
          toast.success('list deleted successfully.')
        } catch (error) {
          toast.error(getErrorMessage(error as ErrorType))
        }
      },
    }),

    fetchListComments: builder.query<
      ListCommentsResponseData,
      { id: string; query: QueryProps }
    >({
      query: ({ id, query }) => ({
        url: `v1/lists/${id}`,
        method: 'GET',
        params: {
          ...query,
        },
      }),
      providesTags: (_result, _error, arg) => [{ type: 'COMMENT', id: arg.id }],
      transformResponse: (data: ListCommentsResponseData) => {
        return data
      },
    }),
    fetchAllListArticles: builder.query<
      ListArticlesResponse,
      {
        query: QueryProps & {
          fulfilmentCentre?: string
          status?: 'ARCHIVED' | 'PUBLISHED' | 'DRAFT'
          type?: 'LIFESTYLE' | 'FOOD' | 'EVENT'
        }
      }
    >({
      query: ({ query }) => ({
        url: `v1/articles`,
        method: 'GET',
        params: {
          ...query,
        },
      }),
      providesTags: [{ type: 'ARTICLE', id: 'LIST' }],
      transformResponse: (data: ListArticlesResponse) => {
        return data
      },
    }),
    fetchSingleListArticle: builder.query<
      SingleListArticleResponse,
      { id: string }
    >({
      query: ({ id }) => ({
        url: `v1/articles/${id}`,
        method: 'GET',
      }),
      providesTags: (_result, _error, arg) => [{ type: 'ARTICLE', id: arg.id }],
      transformResponse: (data: SingleListArticleResponse) => {
        return data
      },
      async onQueryStarted(arg, { queryFulfilled }) {
        try {
          await queryFulfilled
        } catch (error) {
          toast.error(getErrorMessage(error as ErrorType))
        }
      },
    }),
    createListArticle: builder.mutation<
      SingleListArticleResponse,
      CreateArticle
    >({
      query: (payload) => ({
        url: `v1/articles`,
        method: 'POST',
        body: payload,
      }),
      invalidatesTags: [{ type: 'ARTICLE', id: 'LIST' }],
      transformResponse: (data: SingleListArticleResponse) => {
        return data
      },
      async onQueryStarted(arg, { queryFulfilled }) {
        try {
          const data = await queryFulfilled
          toast.success(data.data.message)
        } catch (error) {
          toast.error(getErrorMessage(error as ErrorType))
        }
      },
    }),
    editListArticle: builder.mutation<
      EditedListArticleResponse,
      {
        id: string
        data: { article?: Partial<CreateArticle>; status?: ArticleStatus }
      }
    >({
      query: ({ id, data }) => ({
        url: `v1/articles/${id}`,
        method: 'PATCH',
        body: { ...data },
      }),
      invalidatesTags: (_result, _error, arg) => [
        { type: 'ARTICLE', id: arg.id },
        { type: 'ARTICLE', id: 'LIST' },
      ],
      transformResponse: (data: EditedListArticleResponse) => {
        return data
      },
      async onQueryStarted(arg, { queryFulfilled }) {
        try {
          const data = await queryFulfilled
          toast.success(data.data.message)
        } catch (error) {
          toast.error(getErrorMessage(error as ErrorType))
        }
      },
    }),
    deleteListArticle: builder.mutation<
      DeleteListArticleResponse,
      { id: string }
    >({
      query: ({ id }) => ({
        url: `v1/articles/${id}`,
        method: 'DELETE',
      }),
      invalidatesTags: (_result, _error, arg) => [
        { type: 'ARTICLE', id: arg.id },
        { type: 'ARTICLE', id: 'LIST' },
      ],
      transformResponse: (data: DeleteListArticleResponse) => {
        return data
      },
      async onQueryStarted(arg, { queryFulfilled }) {
        try {
          const data = await queryFulfilled
          toast.success(data.data.message)
        } catch (error) {
          toast.error(getErrorMessage(error as ErrorType))
        }
      },
    }),
    reorderArticles: builder.mutation<
      BaseApiResponse,
      { articleIds: string[] }
    >({
      query: (payload) => ({
        url: `v1/articles/reorder`,
        method: 'POST',
        body: payload,
      }),
      invalidatesTags: [{ type: 'ARTICLE', id: 'LIST' }],
      async onQueryStarted(arg, { queryFulfilled }) {
        try {
          await queryFulfilled
        } catch (error) {
          toast.error(getErrorMessage(error as ErrorType))
        }
      },
    }),
  }),
})

export const {
  useEditListMutation,
  useCreateListMutation,
  useFetchListCategoriesQuery,
  useAddProductsToListMutation,
  useUpdateProductsInListMutation,
  useFetchListQuery,
  useDeleteListMutation,
  useFetchListsQuery,
  useLazyFetchListsQuery,
  useFetchListCommentsQuery,
  useFetchAllListArticlesQuery,
  useLazyFetchAllListArticlesQuery,
  useFetchSingleListArticleQuery,
  useCreateListArticleMutation,
  usePinUnpinListMutation,
  useMoveListMutation,
  useReorderEverydayListMutation,
  useGetListProductsQuery,
  useEditListArticleMutation,
  useDeleteListArticleMutation,
  usePublishUnpublishListMutation,
  useReorderArticlesMutation,
} = listsApiSlice
